import { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { COMMENT_ATTACHMENT_EXTENSIONS } from '@entities/comment';
import { MessageChat } from '@entities/message';
import { FileDropZone, Lightbox, Popup, UiButton, UiText } from '@shared/ui';

import cls from './TaskComments.module.css';
import { TaskCommentsFooter } from './TaskCommentsFooter';
import { useGetTaskCommentsPolling } from '../api/useGetTaskCommentsPolling';
import { useGetTaskCommentsQuery } from '../api/useGetTaskCommentsQuery';
import { resolveTaskCommentDeleteError } from '../model/resolveTaskCommentDeleteError';
import { useTaskCommentDeletion } from '../model/useTaskCommentDeletion';
import { useTaskCommentForm } from '../model/useTaskCommentForm';

import type { IMessageAttachment } from '@entities/message';
import type { IPreviewFile } from '@shared/ui/Lightbox/model/types';

interface TaskCommentsProps {
    taskId: string;
}

export const TaskComments = ({ taskId }: TaskCommentsProps) => {
    const { t } = useTranslation();
    const chatRef = useRef<HTMLElement>(null);
    const { data, isPending, isError, isFetching, refetch } = useGetTaskCommentsQuery(taskId);
    useGetTaskCommentsPolling({
        taskId,
        stateToken: data?.stateToken,
        enabled: Boolean(data) && !isFetching,
    });
    const deletion = useTaskCommentDeletion(taskId);
    const commentForm = useTaskCommentForm(taskId, isPending || deletion.isPending);
    const [previewFiles, setPreviewFiles] = useState<IPreviewFile[]>([]);
    const [previewId, setPreviewId] = useState<number | null>(null);
    const deleteErrorMessage = deletion.error
        ? resolveTaskCommentDeleteError(deletion.error, t('Errors.UnknownError.text'))
        : '';

    const openAttachments = (attachments: IMessageAttachment[], attachmentId: number) => {
        setPreviewFiles(
            attachments.map((file) => ({
                id: file.id,
                fileSrc: file.originalUrl || file.previewUrl || '',
                fileName: file.name,
                mimeType: file.mimeType,
            }))
        );
        setPreviewId(attachmentId);
    };

    return (
        <aside className={cls.Panel}>
            <FileDropZone
                className={cls.DropZone}
                dragActiveClassName={cls.DropOverlay}
                overlayHintClassName={cls.DropHint}
                overlayText={t('TaskComments.FileDropZone.DropHintText')}
                accept={COMMENT_ATTACHMENT_EXTENSIONS.map((extension) => `.${extension}`).join(',')}
                disabled={commentForm.isPending}
                onFilesDrop={commentForm.addFiles}
            >
                {({ openFileDialog }) => (
                    <>
                        {isError && (
                            <div className={cls.Error} role="alert">
                                <UiText variant="mediumPlain">{t('awesome.modal.errorLoading.text')}</UiText>
                                <UiButton
                                    btnText={t('button.retry')}
                                    disabled={isFetching}
                                    onClick={() => {
                                        void refetch();
                                    }}
                                />
                            </div>
                        )}
                        {(!isError || data) && (
                            <MessageChat
                                innerRef={chatRef}
                                className={cls.MessageChatWrapper}
                                messagesData={data?.comments ?? []}
                                isInitialLoading={isPending}
                                onDeleteMessage={commentForm.isPending ? undefined : deletion.openConfirmation}
                                footer={
                                    <TaskCommentsFooter
                                        containerRef={chatRef}
                                        text={commentForm.text}
                                        files={commentForm.files}
                                        isInvalid={commentForm.isInvalid}
                                        isSubmitting={commentForm.isSubmitting}
                                        isPending={commentForm.isPending}
                                        errorMessage={commentForm.errorMessage}
                                        fieldErrors={commentForm.fieldErrors}
                                        onTextChange={commentForm.changeText}
                                        onAttachFiles={openFileDialog}
                                        onRemoveFile={commentForm.removeFile}
                                        onSubmit={commentForm.submit}
                                    />
                                }
                                onOpenAttachments={openAttachments}
                            />
                        )}
                    </>
                )}
            </FileDropZone>
            <Popup
                isOpen={deletion.isOpen}
                type="delete"
                // title={t('Comments.DeleteItem.Confirm.text')}
                contentText={t('Comments.DeleteItem.Confirm.text')}
                isPending={deletion.isPending || commentForm.isSubmitting}
                btnActionCb={() => {
                    if (commentForm.isSubmitting) return;
                    deletion.confirmDeletion();
                }}
                onHidePopup={deletion.closeConfirmation}
            />
            <Popup
                isOpen={!deletion.isOpen && deletion.isError}
                type="alert"
                contentText={deleteErrorMessage}
                onHidePopup={deletion.closeError}
            />
            {previewId !== null && (
                <Lightbox
                    files={previewFiles}
                    currentFileId={previewId}
                    onClose={() => setPreviewId(null)}
                />
            )}
        </aside>
    );
};
